import axiosClient from "utils/axios";
import { ApiList, ApiResponse } from "types/api.types";
import type {
  Holiday,
  SaveHolidayPayload,
  WeeklyOffsPayload,
  WeeklyOffsResult,
} from "../types/holiday.types";
import { normalizeHoliday } from "./holidayNormalize";

function mapList(
  data: ApiList<unknown[]> | undefined,
): ApiList<Holiday[]> {
  if (!data?.rows) {
    return {
      rows: [],
      totalItems: 0,
      currentPage: 1,
      pageSize: 10,
      limit: 10,
      totalPages: 0,
    };
  }
  const rows = (data.rows as unknown[])
    .map((r) => normalizeHoliday(r))
    .filter((r): r is Holiday => r != null);
  return { ...data, rows };
}

export const getHolidays = async ({
  page = 1,
  limit = 10,
  year,
  month,
}: {
  page?: number;
  limit?: number;
  search?: string;
  year?: number;
  month?: number | null;
} = {}) => {
  const response = await axiosClient.get<ApiResponse<ApiList<unknown[]>>>(
    "/holidays",
    {
      params: {
        page,
        limit,
        year:
          year != null && Number.isFinite(year) && year > 1900
            ? year
            : undefined,
        month:
          month != null && Number.isFinite(month) && month >= 1 && month <= 12
            ? month
            : undefined,
      },
    },
  );
  const body = response.data;
  if (!body.data) return body as ApiResponse<ApiList<Holiday[]>>;
  return {
    ...body,
    data: mapList(body.data),
  };
};

export const getHoliday = async (id: number) => {
  const response = await axiosClient.get<ApiResponse<unknown>>(
    `/holidays/${id}`,
  );
  const body = response.data;
  if (!body.data) return body as ApiResponse<Holiday | null>;
  return {
    ...body,
    data: normalizeHoliday(body.data),
  };
};

function buildBody(values: SaveHolidayPayload): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: values.name.trim(),
    date: values.date,
    kind: values.kind,
    isPaid: values.isPaid,
    applicableToAll: values.applicableToAll,
  };
  if (!values.applicableToAll) {
    body.employmentCategoryIds = values.employmentCategoryIds ?? [];
  }
  return body;
}

export const createHoliday = async (payload: SaveHolidayPayload) => {
  const response = await axiosClient.post<ApiResponse<unknown>>(
    "/holidays",
    buildBody(payload),
  );
  const body = response.data;
  if (!body.data) return body as ApiResponse<Holiday | null>;
  return {
    ...body,
    data: normalizeHoliday(body.data),
  };
};

export const updateHoliday = async (
  id: number,
  payload: SaveHolidayPayload,
) => {
  const response = await axiosClient.put<ApiResponse<unknown>>(
    `/holidays/${id}`,
    buildBody(payload),
  );
  const body = response.data;
  if (!body.data) return body as ApiResponse<Holiday | null>;
  return {
    ...body,
    data: normalizeHoliday(body.data),
  };
};

export const deleteHoliday = async (id: number) => {
  const response = await axiosClient.delete<ApiResponse<unknown>>(
    `/holidays/${id}`,
  );
  return response.data;
};

export const bulkDeleteHolidays = async (ids: number[]) => {
  const response = await axiosClient.delete<ApiResponse<{ deleted: number }>>(
    "/holidays",
    { data: { ids } },
  );
  return response.data;
};

export const generateWeeklyOffs = async (payload: WeeklyOffsPayload) => {
  const response = await axiosClient.post<ApiResponse<WeeklyOffsResult>>(
    "/holidays/weekly-offs",
    {
      year: payload.year,
      weekdays: payload.weekdays,
      ...(payload.kind ? { kind: payload.kind } : {}),
    },
  );
  return response.data;
};
